import {
  ApiResult
} from '@/services/data'
import ServiceBase from '@/services/serviceBase'
import utility from '@/common/utility'

export interface FlexTrade {
  SymbolName: string;
  LocalSymbol: string;
  AccountId: string;
  SecType: string;
  TradeDate: string;
  Quantity: number;
  TradePrice: number;
  IbCommission: number;
  Strike: string;
  Right: string;
  Expiry: string;
  BuySell: string;
  Currency: string;
  Rate: number;
  LastUpdated: string;
  Multiplier: number;
  IsExercised: boolean;
  IsExp: boolean;
}

export interface FlexDividend {
  SymbolName: string;
  AccountId: string;
  ExDate: string;
  Fee: number;
  GrossRate: number;
  Quantity: number;
  GrossAmount: number;
  Tax: number;
  Currency: string;
  Rate: number;
  LastUpdated: string;
}

export interface FlexBalance {
  SymbolName: string;
  AccountId: string;
  Description: string;
  ListingExchange: string;
  Currency: string;
  Position: number;
  CostBasisPrice: number;
  LastUpdated: string;
  FromDate: string;
  ToDate: string;
}

export interface FlexOptionExercise {
  SymbolName: string;
  AccountId: string;
  Date: string;
  TransactionType: string;
  PutCall: string;
  Expiry: string;
  Quantity: number;
  TradePrice: number;
  Proceeds: number;
  RealizedPnl: string;
  MtmPnl: string;
  Currency: string;
  LastUpdated: string;
}

export interface FlexStockSplit {
  SymbolName: string;
  AccountId: string;
  Currency: string;
  AcctAlias?: string;
  Model?: string;
  AssetCategory?: string;
  SubCategory?: string;
  Description?: string;
  Conid?: string;
  SecurityId?: string;
  SecurityIdType?: string;
  ListingExchange?: string;
  IssuerCountryCode?: string;
  Multiplier?: number;
  PrincipalAdjustFactor?: number;
  ReportDate: string;
  DateTime?: string;
  ActionDescription?: string;
  Quantity: number;
  Type?: string;
  TransactionId?: string;
  ActionId?: string;
  LevelOfDetail?: string;
  UnderlyingConid?: string;
  UnderlyingSymbol?: string;
  UnderlyingSecurityId?: string;
  UnderlyingListingExchange?: string;
  Issuer?: string;
  Strike?: number;
  Expiry?: string;
  PutCall?: string;
  Amount?: number;
  Proceeds?: number;
  Value?: number;
  FifoPnlRealized?: number;
  MtmPnl?: number;
  Code?: string;
  SerialNumber?: string;
  DeliveryType?: string;
  CommodityType?: string;
  Fineness?: number;
  Weight?: number;
  LastUpdated: string;
}

export interface FlexStockTransfer {
  SymbolName: string;
  AccountId: string;
  Currency: string;
  TransactionId?: number;
  AcctAlias?: string;
  Model?: string;
  FxRateToBase: number;
  AssetCategory?: string;
  SubCategory?: string;
  Description?: string;
  Conid: number;
  SecurityId?: string;
  SecurityIdType?: string;
  Cusip?: string;
  Isin?: string;
  Figi?: string;
  ListingExchange?: string;
  UnderlyingConid?: number;
  UnderlyingSymbol?: string;
  UnderlyingSecurityId?: string;
  UnderlyingListingExchange?: string;
  Issuer?: string;
  IssuerCountryCode?: string;
  Multiplier: number;
  Strike?: number;
  Expiry?: string;
  PutCall?: string;
  PrincipalAdjustFactor?: number;
  ReportDate: string;
  Date: string;
  DateTime?: string;
  SettleDate: string;
  Type?: string;
  Direction?: string;
  Company?: string;
  Account?: string;
  AccountName?: string;
  DeliveringBroker?: string;
  Quantity: number;
  TransferPrice: number;
  PositionAmount: number;
  PositionAmountInBase: number;
  PnlAmount: number;
  PnlAmountInBase: number;
  CashTransfer: number;
  Code?: string;
  ClientReference?: string;
  LevelOfDetail?: string;
  PositionInstructionId?: string;
  PositionInstructionSetId?: string;
  SerialNumber?: string;
  DeliveryType?: string;
  CommodityType?: string;
  Fineness: number;
  Weight: number;
  LastUpdated: string;
}

export interface BaseDayProfit {
  TotalRealized?: number;
  Day: string;
  Realized: number;
  Unrealized: number;
  Dividend?: number;
  RealizedDesc?: string;
  UnrealizedDesc?: string;
}

export interface ProfitCalc {
  Total: { Count: number; Realized: number; Unrealized: number };
  Profits: SymbolProfit[];
}

export interface SymbolProfit {
  Symbol: string;
  DayProfit: BaseDayProfit[];
}

export interface DayProfitResult {
  Option: ProfitCalc;
  Stock: ProfitCalc;
  Dividend: FlexDividend[];
}

export default {
  async getTrades (type: string, forceUpdate: boolean, calcExercised = false): Promise<ApiResult<FlexTrade[]>> {
    const trades = await ServiceBase.tryPostResult<FlexTrade[]>('/Report/GetTrades', {
      ContractType: [type],
      ForceUpdate: forceUpdate,
      CalcExercised: calcExercised
    })
    return trades
  },
  async getDayProfits (symbols: string[], start: string, end: string, contractType?: string[], includeFee?: boolean): Promise<ApiResult<DayProfitResult>> {
    const trades = await ServiceBase.tryPostResult<DayProfitResult>('/Report/GetDayProfits', {
      Symbol: utility.parseSymbols(symbols),
      Start: start,
      End: end,
      ContractType: contractType,
      IncludeFee: includeFee
    })
    return trades
  },
  async getSingleDayProfits (day: string, contractType: string): Promise<ApiResult<FlexTrade[]>> {
    const trades = await ServiceBase.tryPostResult<FlexTrade[]>('/Report/getDayTradeDetail', {
      Day: day,
      ContractType: contractType
    })
    return trades
  },
  async uploadExcel (file: File, excelType: string) {
    return await ServiceBase.tryUploadResult<boolean>('/Report/UploadExcel', {
      file: file,
      excelType: excelType
    })
  },
  async uploadXml (file: File) {
    return await ServiceBase.tryUploadResult<boolean>('/Report/UploadXml', {
      file: file
    })
  },
  async downloadSample (name: string) {
    return await ServiceBase.tryDownload('/Report/downloadSample', {
      Name: name
    })
  },
  async download (type: string) {
    return await ServiceBase.tryDownload('/Report/download', {
      ContractType: [type]
    })
  },
  async getLastSyncDate () {
    const desc = await ServiceBase.tryPostResult<string>('/Report/getLastSyncDate')
    return desc.Result
  },
  async getDividends (forceUpdate: boolean): Promise<ApiResult<FlexDividend[]>> {
    const trades = await ServiceBase.tryPostResult<FlexDividend[]>('/Report/GetDividends', {
      ForceUpdate: forceUpdate
    })
    return trades
  },
  async getBalances (forceUpdate: boolean, updateLastYear?: boolean): Promise<ApiResult<FlexBalance[]>> {
    const trades = await ServiceBase.tryPostResult<FlexBalance[]>('/Report/GetBalances', {
      ForceUpdate: forceUpdate,
      UpdateLastYear: updateLastYear
    })
    return trades
  },
  async getOptionExercise (forceUpdate: boolean): Promise<ApiResult<FlexOptionExercise[]>> {
    const trades = await ServiceBase.tryPostResult<FlexOptionExercise[]>('/Report/getOptionExercise', {
      ForceUpdate: forceUpdate
    })
    return trades
  },
  async getStockTransfers (forceUpdate: boolean): Promise<ApiResult<FlexStockTransfer[]>> {
    const trades = await ServiceBase.tryPostResult<FlexStockTransfer[]>('/Report/GetStockTransfers', {
      ForceUpdate: forceUpdate
    })
    return trades
  },
  async getStockSplits (forceUpdate: boolean): Promise<ApiResult<FlexStockSplit[]>> {
    const trades = await ServiceBase.tryPostResult<FlexStockSplit[]>('/Report/GetStockSplits', {
      ForceUpdate: forceUpdate
    })
    return trades
  },
  async getLastYearTrades (): Promise<ApiResult<boolean>> {
    const trades = await ServiceBase.tryPostResult<boolean>('/Report/GetLastYearTrades')
    return trades
  },
  async getLastYearBalances (): Promise<ApiResult<boolean>> {
    const trades = await ServiceBase.tryPostResult<boolean>('/Report/GetLastYearBalances')
    return trades
  },
  async getLastYearOptionExercises (): Promise<ApiResult<boolean>> {
    const trades = await ServiceBase.tryPostResult<boolean>('/Report/GetLastYearOptionExercises')
    return trades
  },
  async getLastYearDividends (): Promise<ApiResult<boolean>> {
    const trades = await ServiceBase.tryPostResult<boolean>('/Report/GetLastYearDividends')
    return trades
  },
  async getLastYearStockSplits (): Promise<ApiResult<boolean>> {
    const trades = await ServiceBase.tryPostResult<boolean>('/Report/GetLastYearStockSplits')
    return trades
  },
  async getLastYearStockTransfers (): Promise<ApiResult<boolean>> {
    const trades = await ServiceBase.tryPostResult<boolean>('/Report/GetLastYearStockTransfers')
    return trades
  }
}
